import React, { useEffect, useRef, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  TextField,
  CardContent,
  Card,
  Tooltip,
} from "@mui/material";
import { columns, tabLabels, componentSettings } from "./columns";
import { useInstrumentation } from "../../InstrumentationContext";
import {
  estimate,
  round2,
  UNITS_ENUM,
  isLengthBased,
  getUnitForComponent,
} from "./utils";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { secondAxios } from "../../../../../../api/axios";

import { MaterialReactTable } from "material-react-table";
import {
  mrtBodyCellSx,
  mrtHeadCellSx,
  mrtSurfaceProps,
} from "../../../../../../styles/mrtThemeProps";
// ─── Affected columns per unit system (for tooltip) ─────────────────────────
const PIPING_UNIT_COLS = [
  { label: "Unit Weight", imp: "lbs/unit", met: "kg/unit" },
  { label: "Unit Cost", imp: "$/unit or $/lb", met: "$/unit or $/kg" },
  { label: "Total Weight", imp: "lbs", met: "kg" },
  { label: "BOM Weight", imp: "lb", met: "kg" },
];

// The two panels sit side by side and must line up row-for-row, so every
// vertical metric that affects alignment is fixed and shared between them:
// same header height, same row height, same viewport height. Anything that
// lets one panel's row grow (wrapping text, a taller control) breaks the
// alignment that the synced scrolling depends on.
const PANEL_ROW_HEIGHT = 45;
const PANEL_HEAD_HEIGHT = 56;
const PANEL_BODY_MAX_HEIGHT = 470;

// Estimated values only ever shows short numbers, so it gets a fixed narrow
// width — just enough for its columns at their declared sizes, with none of
// the extra stretch space that was padding them out. Everything left over
// goes to Bulk/Tag Materials, whose text is what actually needs the room.
const ESTIMATED_PANEL_WIDTH = 380;

// Every cell — plain text, text inputs and comboboxes — renders at this size.
const PANEL_FONT_SIZE = "0.65rem";

/*
 * MRT merges cell props with a shallow Object.assign, so a column's own
 * `muiTableBodyCellProps.sx` REPLACES the table-level one rather than
 * merging into it. Every column here carries an sx (columns.jsx adds one),
 * which means table-level cell styling never reaches them. So the shared
 * panel styling has to be folded into each column definition instead.
 */
const panelBodyCellSx = (isFirstPanel) => ({
  ...mrtBodyCellSx,
  // No vertical rule between columns — the spacing alone separates them.
  borderRight: "none",
  height: `${PANEL_ROW_HEIGHT}px`,
  maxHeight: `${PANEL_ROW_HEIGHT}px`,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: isFirstPanel ? "4px" : "2px",
  paddingRight: isFirstPanel ? "4px" : "2px",
  boxSizing: "border-box",
  // columns.jsx pins a per-column min/max width, which fights the fixed
  // table layout and forces a horizontal scrollbar. Clear both.
  minWidth: 0,
  maxWidth: "none",
  // One line per cell. Without this a long value ("Screwed x SW") wraps and
  // makes just that row taller than its opposite number in the other panel.
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "center",
  fontSize: PANEL_FONT_SIZE,
  lineHeight: 1.2,

  // Keep every control short enough to sit inside the fixed row height, and
  // pin them all to one font size so text, inputs and comboboxes match.
  "& .MuiInputBase-root": {
    minHeight: "26px",
    height: "26px",
    fontSize: `${PANEL_FONT_SIZE} !important`,
  },
  "& .MuiInputBase-input": {
    paddingTop: "2px",
    paddingBottom: "2px",
    fontSize: `${PANEL_FONT_SIZE} !important`,
  },
  "& .MuiSelect-select": {
    minHeight: "unset !important",
    paddingTop: "2px !important",
    paddingBottom: "2px !important",
    display: "flex",
    alignItems: "center",
    fontSize: `${PANEL_FONT_SIZE} !important`,
    // EditableSelectCell asks for wrapping text; inside a fixed-height row
    // it has to stay on one line instead.
    whiteSpace: "nowrap !important",
    overflow: "hidden !important",
    textOverflow: "ellipsis",
    wordBreak: "normal !important",
  },
  "& .MuiSelect-icon": { fontSize: "0.9rem", right: 0 },
  "& .MuiButton-root": {
    minHeight: "26px",
    height: "26px",
    fontSize: `${PANEL_FONT_SIZE} !important`,
  },
  "& .MuiIconButton-root": { padding: "2px" },
  "& .MuiSvgIcon-root": { fontSize: "1rem" },
});

// A fixed header height in both panels. Estimated values' labels run to three
// lines and Tag Materials' to one or two — left alone, the two bodies would
// start at different offsets and no amount of scroll syncing would line the
// rows up.
const panelHeadCellSx = {
  ...mrtHeadCellSx,
  fontWeight: "bold",
  fontSize: PANEL_FONT_SIZE,
  borderRight: "none",
  height: `${PANEL_HEAD_HEIGHT}px`,
  maxHeight: `${PANEL_HEAD_HEIGHT}px`,
  paddingTop: "2px",
  paddingBottom: "2px",
  paddingLeft: "4px",
  paddingRight: "4px",
  verticalAlign: "middle",
  boxSizing: "border-box",
  minWidth: 0,
  maxWidth: "none",
  "& .Mui-TableHeadCell-Content": {
    justifyContent: "center",
    height: "100%",
    alignItems: "center",
  },
  "& .Mui-TableHeadCell-Content-Wrapper": {
    whiteSpace: "normal",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.15,
    width: "100%",
    textAlign: "center",
  },
};

// Fold the shared panel styling into each column, letting it win over the
// column's own sx for the keys that decide alignment.
function withPanelCellStyles(tableColumns, isFirstPanel) {
  const bodySx = panelBodyCellSx(isFirstPanel);

  return tableColumns.map((col) => ({
    ...col,
    muiTableBodyCellProps: {
      ...(col.muiTableBodyCellProps || {}),
      sx: { ...(col.muiTableBodyCellProps?.sx || {}), ...bodySx },
    },
    muiTableHeadCellProps: {
      ...(col.muiTableHeadCellProps || {}),
      sx: { ...(col.muiTableHeadCellProps?.sx || {}), ...panelHeadCellSx },
    },
  }));
}

// ─── Normalize legacy unit values to the correct select option values ─────────
function normalizeUnit(unit) {
  if (!unit) return "";
  switch (unit) {
    case "Ft":
      return "ft";
    case "M":
      return "m";
    case "Ft^2":
    case "Ft²":
      return "ft²";
    case "M^2":
    case "M²":
      return "m²";
    case "No.":
    case "Nos.":
      return "Nos";
    default:
      return unit; // already correct
  }
}

function UnitToggle({ isMetric, onChange }) {
  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Box
        sx={{
          fontSize: "0.7rem",
          color: "text.secondary",
          mb: 0.75,
          fontWeight: 500,
        }}
      >
        Columns that change
      </Box>
      {PIPING_UNIT_COLS.map((c) => (
        <Box
          key={c.label}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            fontSize: "0.7rem",
            py: "2px",
          }}
        >
          <span style={{ opacity: 0.75 }}>{c.label}</span>
          <span style={{ fontWeight: 500 }}>{isMetric ? c.met : c.imp}</span>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="bottom-start" arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          border: "0.5px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          cursor: "default",
        }}
      >
        {/* Imperial button */}
        <Box
          onClick={() => onChange(false)}
          sx={{
            px: 1.5,
            py: 0.6,
            fontSize: "0.8rem",
            fontWeight: isMetric ? 400 : 500,
            color: isMetric ? "text.secondary" : "primary.main",
            bgcolor: isMetric ? "transparent" : "action.selected",
            cursor: "pointer",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: isMetric ? "action.hover" : "action.selected",
            },
          }}
        >
          Imperial
          <Box
            component="span"
            sx={{
              fontSize: "0.65rem",
              px: 0.75,
              py: "1px",
              borderRadius: 99,
              bgcolor: isMetric ? "transparent" : "#E6F1FB",
              color: isMetric ? "text.disabled" : "#185FA5",
              fontWeight: 500,
              transition: "all 0.15s",
            }}
          >
            ft · lb
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ width: "0.5px", height: 20, bgcolor: "divider" }} />

        {/* Metric button */}
        <Box
          onClick={() => onChange(true)}
          sx={{
            px: 1.5,
            py: 0.6,
            fontSize: "0.8rem",
            fontWeight: isMetric ? 500 : 400,
            color: isMetric ? "primary.main" : "text.secondary",
            bgcolor: isMetric ? "action.selected" : "transparent",
            cursor: "pointer",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: isMetric ? "action.selected" : "action.hover",
            },
          }}
        >
          Metric
          <Box
            component="span"
            sx={{
              fontSize: "0.65rem",
              px: 0.75,
              py: "1px",
              borderRadius: 99,
              bgcolor: isMetric ? "#E1F5EE" : "transparent",
              color: isMetric ? "#0F6E56" : "text.disabled",
              fontWeight: 500,
              transition: "all 0.15s",
            }}
          >
            m · kg
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}

export function EstimationTable({ revision }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [fittingsWeld, setFittingsWeld] = useState(1);
  const [pipeWeld, setPipeWeld] = useState(1);
  const [isMetric, setIsMetric] = useState(false);
  const [showEstimatedBulkTable, setShowEstimatedBulkTable] = useState(false);
  const currentTab = tabLabels[tabIndex];
  const { id } = useParams();

  // ─── Synced vertical scrolling between the two side-by-side panels ─────────
  // Both tables render the same rows at the same fixed row height, so mirroring
  // scrollTop keeps a row on the left visually level with its estimated values
  // on the right. The guard stops the mirrored scroll from echoing back.
  const firstPanelScrollRef = useRef(null);
  const secondPanelScrollRef = useRef(null);
  const isSyncingScrollRef = useRef(false);

  const syncPanelScroll = (sourceRef, targetRef) => () => {
    if (isSyncingScrollRef.current) return;
    const source = sourceRef.current;
    const target = targetRef.current;
    if (!source || !target || source.scrollTop === target.scrollTop) return;

    isSyncingScrollRef.current = true;
    target.scrollTop = source.scrollTop;
    // Release on the next frame, after the mirrored scroll event has fired.
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  };

  const {
    getSelectedSpecTable,
    SelectedParts,
    allTables,
    estimationTable,
    setEstimationTable,
    totalSummary,
    setTotalSummary,
    dataSheetsStore,
    uidOptions,
    uidRows,
    specLibraryTable,
  } = useInstrumentation();

  // Always start each revision with Estimated values hidden, even if saved
  // estimation values already exist in state. It is shown only by Estimate.
  useEffect(() => {
    setShowEstimatedBulkTable(false);
  }, [revision]);

  // ─── Convert DB fields in state when toggling unit system ───────────────────
  // DB stores metric (kg, $/kg). On toggle we convert Weight, UnitCost,
  // TotalWeight in both Bulk and Tag rows so columns can render raw values.
  function handleUnitToggle(newIsMetric) {
    if (newIsMetric === isMetric) return;

    const toMetric = newIsMetric; // true  = going metric
    const weightFactor = toMetric ? 1 / 2.20462 : 2.20462; // lbs↔kg
    // const costFactor = toMetric ? 2.20462 : 1 / 2.20462; // $/lb↔$/kg

    const convertRow = (row) => {
      const next = { ...row };

      // Re-derive unit based on component type and new unit system
      next.Unit = getUnitForComponent(next.Components, toMetric);

      if (
        next.Weight !== undefined &&
        next.Weight !== null &&
        next.Weight !== "" &&
        next.Weight !== "N/A"
      ) {
        next.Weight = +(Number(next.Weight) * weightFactor).toFixed(4);
      }
      // Only convert UnitCost if the unit is length-based ($/kg ↔ $/lb)
      // Nos items are priced $/unit so cost doesn't change with unit system
      const isNos = !isLengthBased(next.Components);

      if (
        next.TotalWeight !== undefined &&
        next.TotalWeight !== null &&
        next.TotalWeight !== "" &&
        next.TotalWeight !== "N/A"
      ) {
        next.TotalWeight = +(Number(next.TotalWeight) * weightFactor).toFixed(
          4,
        );
      }

      return next;
    };

    setEstimationTable((prev) => {
      const rev = prev?.[revision] || {};

      const freshBulk = (rev["Bulk Materials"] || []).map(convertRow);
      const freshTag = (rev["Tag Materials"] || []).map(convertRow);

      // Regenerate BOM from the already-converted rows so totWeight stays in sync
      const freshBOM = generateBOMData(freshBulk, freshTag);

      // Update baseUnit on Summary rows to reflect the new unit system
      const freshSummary = (rev["Summary"] || []).map((r) => {
        if (!r.MatDescription) return r;
        // Find the source row in bulk/tag to get the correct component name
        const sourceRow = [...freshBulk, ...freshTag].find(
          (row) => row["Material Description"] === r.MatDescription,
        );
        const newUnit = sourceRow
          ? getUnitForComponent(sourceRow.Components, toMetric)
          : r.baseUnit;
        if (r.baseUnit === newUnit) return r;
        return { ...r, baseUnit: newUnit };
      });

      return {
        ...prev,
        [revision]: {
          ...rev,
          "Bulk Materials": freshBulk,
          "Tag Materials": freshTag,
          Summary: freshSummary,
          BOM: freshBOM,
        },
      };
    });

    setIsMetric(newIsMetric);
  }

  // ─── Totals — values in state already reflect current unit system ────────────
  const totalsRaw = estimationTable?.[revision]?.[currentTab]?.reduce(
    (acc, item) => {
      const qty = Number(item.Qty) || 0;
      if (!qty) return acc;

      let totalWeight = item.TotalWeight;
      if (
        totalWeight === "N/A" ||
        totalWeight === undefined ||
        totalWeight === null
      ) {
        totalWeight = 0;
      } else {
        totalWeight = Number(totalWeight) || 0;
      }

      let totalFabHours = item.TotalFabHours;
      if (
        totalFabHours === "N/A" ||
        totalFabHours === undefined ||
        totalFabHours === null
      ) {
        totalFabHours = 0;
      } else {
        totalFabHours = Number(totalFabHours) || 0;
      }

      return {
        weight: acc.weight + totalWeight,
        weldHours: acc.weldHours + totalFabHours,
      };
    },
    { weight: 0, weldHours: 0 },
  ) ?? { weight: 0, weldHours: 0 };

  const totals = {
    weight: round2(totalsRaw.weight),
    weldHours: round2(totalsRaw.weldHours),
  };

  // ─── Auto-assign Unit on both tabs when unit system toggles ─────────────────
  useEffect(() => {
    setEstimationTable((prev) => {
      const tabsToCheck = ["Bulk Materials", "Tag Materials"];
      let updatedRevision = { ...(prev?.[revision] || {}) };
      let changed = false;

      tabsToCheck.forEach((tab) => {
        const rows = updatedRevision[tab] || [];
        if (!rows.length) return;

        let tabChanged = false;
        const updatedRows = rows.map((row) => {
          // First normalize any legacy values (e.g. "Ft" → "ft", "No." → "Nos")
          const normalized = normalizeUnit(row.Unit);
          const correctUnit = getUnitForComponent(row.Components, isMetric);
          const finalUnit = correctUnit || normalized || "";
          if (row.Unit === finalUnit) return row;
          tabChanged = true;
          return { ...row, Unit: finalUnit };
        });

        if (tabChanged) {
          updatedRevision = { ...updatedRevision, [tab]: updatedRows };
          changed = true;
        }
      });

      if (!changed) return prev;

      return { ...prev, [revision]: updatedRevision };
    });
  }, [revision, isMetric]);

  // ─── Fetch & merge unitCost when switching to Summary tab ───────────────────
  useEffect(() => {
    if (currentTab !== "Summary") return;

    const summaryRows = estimationTable?.[revision]?.Summary || [];
    const nonSummaryRows = summaryRows.filter((r) => r.MatDescription);
    if (!nonSummaryRows.length) return;

    const fetchAndMerge = async () => {
      try {
        const { data: materialRecords } =
          await secondAxios.get("/material-record");

        setEstimationTable((prev) => {
          const existing = prev?.[revision]?.Summary || [];

          // Build a set of MatDescriptions that still have qty in bulk/tag
          const activeDescriptions = new Set(
            [
              ...(prev?.[revision]?.["Bulk Materials"] || []),
              ...(prev?.[revision]?.["Tag Materials"] || []),
            ]
              .filter((r) => (Number(r.Qty) || 0) > 0)
              .map((r) => r["Material Description"])
              .filter(Boolean),
          );

          const merged = existing
            .filter(
              (r) =>
                !r.MatDescription || activeDescriptions.has(r.MatDescription),
            )
            .map((r) => {
              if (!r.MatDescription) return r;
              const match = materialRecords?.find((mr) =>
                mr.description?.includes(r.MatDescription),
              );
              return {
                ...r,
                unitCost: match?.estCost ?? r.unitCost ?? "Not Added!",
              };
            });

          return {
            ...prev,
            [revision]: {
              ...prev?.[revision],
              Summary: merged,
            },
          };
        });
      } catch (e) {
        console.error("Failed to fetch material records", e);
      }
    };

    fetchAndMerge();
  }, [currentTab, revision]);

  // ─── Sync Total Materials Cost whenever Summary rows change ─────────────────
  useEffect(() => {
    const rows = (estimationTable?.[revision]?.Summary || []).filter(
      (r) => r.MatDescription,
    );

    const total = rows.reduce((sum, r) => {
      const tc =
        r.procuredQty &&
        r.unitCost &&
        r.unitCost !== "Not Added!" &&
        r.procuredQty !== "-"
          ? Number(r.procuredQty) * Number(r.unitCost)
          : 0;
      return sum + (Number(tc) || 0);
    }, 0);

    setTotalSummary((prev) =>
      (prev || []).map((x) =>
        x.name === "Total Materials Cost"
          ? { ...x, value: total > 0 ? total.toFixed(2) : "" }
          : x,
      ),
    );
  }, [estimationTable, revision]);

  // ─── API ─────────────────────────────────────────────────────────────────────
  const api = {
    getItems: () => estimationTable[revision]?.[currentTab] || [],
  };

  // ─── Handlers ────────────────────────────────────────────────────────────────
  function handleAddRow() {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[revision] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || []), {}];
      return {
        ...prev,
        [revision]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  function handleFieldUpdate(rowIndex, fieldName, value) {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[revision] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || [])];
      updatedTab[rowIndex] = { ...updatedTab[rowIndex], [fieldName]: value };
      return {
        ...prev,
        [revision]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  function handleDeleteRow(rowIndex) {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[revision] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || [])];
      updatedTab.splice(rowIndex, 1);
      return {
        ...prev,
        [revision]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  function handleSummaryRateUpdate(targetCol, value) {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[revision] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || [])];
      const rateRowIndex = updatedTab.length - 3;
      if (rateRowIndex < 0 || !updatedTab[rateRowIndex]?.isSummary) return prev;
      updatedTab[rateRowIndex] = {
        ...updatedTab[rateRowIndex],
        [targetCol]: value,
      };
      return {
        ...prev,
        [revision]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  // ─── Summary generation (accepts fresh rows directly) ───────────────────────
  function generateSummaryData(bulkMaterials, tagMaterials) {
    const allMaterials = [...bulkMaterials, ...tagMaterials];
    const grouped = {};

    allMaterials.forEach((row) => {
      const qty = Number(row.Qty) || 0;
      if (!qty) return; // skip rows with no qty entirely

      const matDesc = row["Material Description"] || "Unknown";
      const unit = row.Unit || "Nos";

      let totalFabHours = row.TotalFabHours;
      if (
        totalFabHours === "N/A" ||
        totalFabHours === undefined ||
        totalFabHours === null
      ) {
        totalFabHours = 0;
      } else {
        totalFabHours = Number(totalFabHours) || 0;
      }

      if (!grouped[matDesc]) {
        grouped[matDesc] = {
          MatDescription: matDesc,
          quant: qty,
          procuredQty: qty,
          baseUnit: unit,
          unitCost: "Not Added!",
          totalFabHrs: totalFabHours,
        };
      } else {
        grouped[matDesc].quant = (Number(grouped[matDesc].quant) || 0) + qty;
        grouped[matDesc].procuredQty =
          (Number(grouped[matDesc].procuredQty) || 0) + qty;
        grouped[matDesc].totalFabHrs =
          (Number(grouped[matDesc].totalFabHrs) || 0) + totalFabHours;
      }
    });

    return Object.values(grouped);
  }

  // ─── BOM generation (accepts fresh rows directly) ────────────────────────────
  function generateBOMData(bulkMaterials, tagMaterials) {
    const allMaterials = [...bulkMaterials, ...tagMaterials];

    return allMaterials
      .filter((row) => (Number(row.Qty) || 0) > 0) // skip rows with no qty entirely
      .map((row, index) => {
        const unit = row.Unit || "No.";
        const qty = Number(row.Qty) || 0;

        let totalWeight = row.TotalWeight;
        if (
          totalWeight === "N/A" ||
          totalWeight === undefined ||
          totalWeight === null
        ) {
          totalWeight = 0;
        } else {
          totalWeight = Number(totalWeight) || 0;
        }

        const isNotNos = unit !== "Nos" && unit !== "ft²" && unit !== "m²";

        // return {
        //   ItemNo: index + 1,
        //   MatDesc: row["Material Description"] || "",
        //   CategoryType: row["CategoryType"] || "",
        //   Quantity: isNotNos ? "-" : qty,
        //   dimension: isNotNos ? qty : "-",
        //   ItemUnit: unit,
        //   totWeight: totalWeight ? totalWeight.toFixed(2) : 0,
        // };
        const bomRow = {
          ItemNo: index + 1,
          MatDesc: row["Material Description"] || "",
          CategoryType: row["CategoryType"] || "",
          Quantity: isNotNos ? "-" : qty,
          dimension: isNotNos ? qty : "-",
          ItemUnit: unit,
          totWeight: totalWeight ? totalWeight.toFixed(2) : 0,
          uid: row["UID"] || "",
          tagNumber:
            row.CategoryType !== "Bulk" ? row["TagNumber"] || "" : undefined,
        };

        console.log("BOM row:", bomRow);
        return bomRow;
      });
  }

  // ─── Estimate ────────────────────────────────────────────────────────────────
  async function handleEstimate() {
    // Fetch material records first so unitCost is always up to date
    let materialRecords = [];

    try {
      const { data } = await secondAxios.get("/material-record");
      materialRecords = data || [];
    } catch (e) {
      console.error("Failed to fetch material records", e);
    }

    // estimate() returns metric — convert every estimated row to imperial
    const toImperial = (row) => {
      if ((Number(row.Qty) || 0) === 0) return row;

      const next = { ...row };

      if (
        next.Weight !== undefined &&
        next.Weight !== null &&
        next.Weight !== "" &&
        next.Weight !== "N/A"
      ) {
        next.Weight = +(Number(next.Weight) * 2.20462).toFixed(4);
      }

      if (
        next.TotalWeight !== undefined &&
        next.TotalWeight !== null &&
        next.TotalWeight !== "" &&
        next.TotalWeight !== "N/A"
      ) {
        next.TotalWeight = +(Number(next.TotalWeight) * 2.20462).toFixed(4);
      }

      return next;
    };

    const currentRevisionData = estimationTable?.[revision] || {};

    const runEstimate = async (tabName) => {
      const rows = currentRevisionData?.[tabName] || [];

      const updatedRows = rows.map((row) => {
        if ((Number(row.Qty) || 0) === 0) {
          return {
            ...row,
            Weight: undefined,
            TotalWeight: undefined,
            TotalFabHours: undefined,
            WeldHour: undefined,
            BoltupHour: undefined,
          };
        }

        return { ...row };
      });

      if (!updatedRows.length) return updatedRows;

      const indexMap = [];

      const rowsToEstimate = updatedRows.filter((row, originalIdx) => {
        if ((Number(row.Qty) || 0) > 0) {
          indexMap.push(originalIdx);
          return true;
        }

        return false;
      });

      if (!rowsToEstimate.length) return updatedRows;

      await estimate(
        rowsToEstimate,
        (subIndex, fieldName, value) => {
          const originalIndex = indexMap[subIndex];

          if (originalIndex === undefined) return;

          updatedRows[originalIndex] = {
            ...updatedRows[originalIndex],
            [fieldName]: value,
          };
        },
        fittingsWeld,
        pipeWeld,
      );

      return updatedRows;
    };

    const freshBulkMetric = await runEstimate("Bulk Materials");
    const freshTagMetric = await runEstimate("Tag Materials");

    // Convert metric → imperial and re-derive units
    const freshBulk = freshBulkMetric.map(toImperial);
    const freshTag = freshTagMetric.map(toImperial);

    // Generate Summary and BOM — both skip rows with no qty
    const summaryData = generateSummaryData(freshBulk, freshTag);
    const bomData = generateBOMData(freshBulk, freshTag);

    // Merge unitCost from material records
    const summaryWithCost = summaryData.map((r) => {
      if (!r.MatDescription) return r;

      const match = materialRecords.find((mr) =>
        mr.description?.includes(r.MatDescription),
      );

      return {
        ...r,
        unitCost: match?.estCost ?? r.unitCost ?? "Not Added!",
      };
    });

    // Compute materials total
    const materialsTotal = summaryWithCost.reduce((sum, r) => {
      const tc =
        r.procuredQty && r.unitCost && r.unitCost !== "Not Added!"
          ? Number(r.procuredQty) * Number(r.unitCost)
          : 0;

      return sum + (Number(tc) || 0);
    }, 0);

    setTotalSummary((prevTS) =>
      (prevTS || []).map((x) =>
        x.name === "Total Materials Cost"
          ? {
              ...x,
              value: materialsTotal > 0 ? materialsTotal.toFixed(2) : "",
            }
          : x,
      ),
    );

    setEstimationTable((prev) => ({
      ...prev,
      [revision]: {
        ...(prev[revision] || {}),
        "Bulk Materials": freshBulk,
        "Tag Materials": freshTag,
        Summary: summaryWithCost,
        BOM: bomData,
      },
    }));

    // Estimated values must only become visible after the user clicks Estimate.
    setShowEstimatedBulkTable(true);

    // Reset toggle to imperial after estimate
    setIsMetric(false);
  }

  // ─── Send BOM to PLM ─────────────────────────────────────────────────────────
  function handleSendBomToPlm() {
    // Read BOM from state (already generated and saved by handleEstimate)
    const bom = estimationTable[revision]?.BOM || [];

    const payload = {
      source: "Estimation",
      softwareId: uuidv4(),
      name: "Instrumentation",
      dwgNumber: "001",
      dwgTitle: "Instrumentation BOM",
      revision: revision,
      version: "673",
      sheetNumber: "0",
      sheetTitle: "Sheet",
      projectId: id,
      materials: bom.map((cur) => ({
        item: cur.ItemNo,
        ref: null,
        description: cur.MatDesc,
        length:
          cur.ItemUnit === UNITS_ENUM.Ft && cur.dimension !== "-"
            ? cur.dimension
            : null,
        quantity:
          cur.Quantity === "-"
            ? null
            : cur.ItemUnit === UNITS_ENUM.no
              ? 1
              : cur.Quantity,
        weightlb: cur.totWeight === "-" ? null : cur.totWeight,
        costCode: null,
        softwareId: uuidv4(),
      })),
    };

    secondAxios.delete("drawing/title/Structure BOM");
    secondAxios.post("drawing", [payload]);
  }

  const allColumnsByTab = columns(
    allTables,
    SelectedParts,
    handleFieldUpdate,
    handleDeleteRow,
    handleSummaryRateUpdate,
    totalSummary,
    setTotalSummary,
    isMetric,
    dataSheetsStore,
    uidOptions,
    uidRows,
    specLibraryTable,
  );

  const allBulkColumns = allColumnsByTab["Bulk Materials"];
  const allTagColumns = allColumnsByTab["Tag Materials"];

  // Keys that belong in the main (input) panel vs. the "Estimated values" panel —
  // shared by both Bulk Materials and Tag Materials so the two tabs look alike.
  const FIRST_PANEL_KEYS = [
    "UID",
    "Spec",
    "Line Number",
    "Components",
    "Size",
    "Schedule/Rating",
    "Ends",
    "Material",
    "Material Description",
    "Qty",
    "Unit",
    "Remarks",
  ];

  const SECOND_PANEL_KEYS = [
    "Weight",
    "WeldHour",
    "BoltupHour",
    "TotalWeight",
    "TotalFabHours",
    "Actions",
  ];

  const firstBulkColumns = withPanelCellStyles(
    allBulkColumns.filter((column) =>
      FIRST_PANEL_KEYS.includes(column.accessorKey || column.id),
    ),
    true,
  );

  const secondBulkColumns = withPanelCellStyles(
    allBulkColumns.filter((column) =>
      SECOND_PANEL_KEYS.includes(column.accessorKey || column.id),
    ),
    false,
  );

  // Tag Materials keeps its own extra columns (Tag Number, Data Sheet) in the
  // main panel alongside the shared fields.
  const firstTagColumns = withPanelCellStyles(
    allTagColumns.filter((column) => {
      const key = column.accessorKey || column.id;
      return (
        FIRST_PANEL_KEYS.includes(key) ||
        key === "TagNumber" ||
        key === "DataSheet"
      );
    }),
    true,
  );

  const secondTagColumns = withPanelCellStyles(
    allTagColumns.filter((column) =>
      SECOND_PANEL_KEYS.includes(column.accessorKey || column.id),
    ),
    false,
  );

  const renderBulkMaterialsTable = (
    tableColumns,
    tableData,
    label,
    scrollRef,
    onScroll,
  ) => {
  // Both the main panel (Bulk/Tag Materials) and the "Estimated values"
  // panel use identical vertical metrics below so their rows stay level.
  const isFirstPanel = label !== "Estimated values";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        // Estimated values is pinned to just enough width for its numeric
        // columns; the materials table takes everything that's left.
        ...(isFirstPanel
          ? { flex: "1 1 0" }
          : {
              flex: `0 0 ${ESTIMATED_PANEL_WIDTH}px`,
              width: `${ESTIMATED_PANEL_WIDTH}px`,
            }),
        minWidth: 0,
      }}
    >
      {label && (
        <Typography
          variant="subtitle2"
          sx={{
            color: "primary.main",
            fontWeight: 600,
            px: 1,
            py: 0.5,
          }}
        >
          {label}
        </Typography>
      )}

      <MaterialReactTable
        columns={tableColumns}
        data={tableData}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enableStickyHeader={true}
        enableSorting={false}
        enableColumnActions={false}
        {...mrtSurfaceProps}

        /*
         * tableLayout: fixed + width: 100% makes the browser scale the
         * columns' declared sizes to exactly fill the panel — so the columns
         * are never spread apart by leftover space, and never overflow into
         * a horizontal scrollbar either.
         */
        muiTableProps={{
          sx: {
            tableLayout: "fixed",
            width: "100%",
          },
        }}

        muiTableContainerProps={{
          ref: scrollRef,
          onScroll,
          sx: {
            height: PANEL_BODY_MAX_HEIGHT,
            maxHeight: PANEL_BODY_MAX_HEIGHT,
            overflowX: "hidden",
            backgroundColor: "background.paper",
          },
        }}

        /*
         * Same fixed row height in both panels — this is what lets the
         * synced scroll keep matching rows on the same visual line.
         */
        muiTableBodyRowProps={{
          sx: {
            height: `${PANEL_ROW_HEIGHT}px !important`,
          },
        }}

        /*
         * These only apply to columns that carry no sx of their own — every
         * real column gets the same styling folded in by withPanelCellStyles
         * above, because MRT's shallow prop merge would otherwise drop it.
         */
        muiTableBodyCellProps={{ sx: panelBodyCellSx(isFirstPanel) }}
        muiTableHeadCellProps={{ sx: panelHeadCellSx }}

        muiTableHeadRowProps={{
          sx: {
            backgroundColor: "background.default",
            height: `${PANEL_HEAD_HEIGHT}px`,
          },
        }}

        initialState={{
          pagination: {
            pageSize: 1000,
            pageIndex: 0,
          },
        }}

        state={{
          columnOrder: tableColumns.map(
            (col) => col.accessorKey || col.id,
          ),
        }}
      />
    </Box>
  );
};

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider" }}
    >
      <Tabs
        value={tabIndex}
        onChange={(e, newIndex) => setTabIndex(newIndex)}
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {tabLabels.map((label) => (
          <Tab label={label} key={label} />
        ))}
      </Tabs>

      <Box
        py={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flex: 1 }}>
          {(currentTab === "Bulk Materials" ||
            currentTab === "Tag Materials") && (
            <>
              <Button
                variant="outlined"
                onClick={handleAddRow}
                sx={{ height: 40, fontSize: "0.9rem", px: 3, fontWeight: 600 }}
              >
                Add Row
              </Button>

              <Button
                variant="contained"
                onClick={handleEstimate}
                sx={{ height: 40, fontSize: "0.9rem", px: 3, fontWeight: 600 }}
              >
                Estimate
              </Button>

              {currentTab === "Bulk Materials" && (
                <>
                  <TextField
                    defaultValue={fittingsWeld}
                    onBlur={(e) => setFittingsWeld(e.target.value)}
                    type="number"
                    label="Fittings to Fittings Weld"
                    size="small"
                    sx={{
                      minWidth: 180,
                      "& .MuiInputBase-root": { fontSize: "0.9rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
                    }}
                  />
                  <TextField
                    defaultValue={pipeWeld}
                    onBlur={(e) => setPipeWeld(e.target.value)}
                    type="number"
                    label="Pipe to Pipe Weld"
                    size="small"
                    sx={{
                      minWidth: 180,
                      "& .MuiInputBase-root": { fontSize: "0.9rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
                    }}
                  />
                </>
              )}

              <UnitToggle isMetric={isMetric} onChange={handleUnitToggle} />
            </>
          )}
        </Box>

        {currentTab === "BOM" && (
          <Button variant="contained" onClick={handleSendBomToPlm}>
            Send BOM to PLM
          </Button>
        )}
      </Box>

      {currentTab === "Bulk Materials" || currentTab === "Tag Materials" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              width: "100%",
              minWidth: 0,
            }}
          >
            {renderBulkMaterialsTable(
              currentTab === "Bulk Materials"
                ? firstBulkColumns
                : firstTagColumns,
              estimationTable[revision]?.[currentTab] || [],
              currentTab,
              firstPanelScrollRef,
              syncPanelScroll(firstPanelScrollRef, secondPanelScrollRef),
            )}

            {showEstimatedBulkTable &&
              renderBulkMaterialsTable(
                currentTab === "Bulk Materials"
                  ? secondBulkColumns
                  : secondTagColumns,
                estimationTable[revision]?.[currentTab] || [],
                "Estimated values",
                secondPanelScrollRef,
                syncPanelScroll(secondPanelScrollRef, firstPanelScrollRef),
              )}
          </Box>
        </Box>
      ) : (
        <MaterialReactTable
          columns={allColumnsByTab[currentTab]}
          data={estimationTable[revision]?.[currentTab] || []}
          enableTopToolbar={true}
          enableStickyHeader={true}
          enableSorting={false}
          {...mrtSurfaceProps}
          muiTableContainerProps={{
            sx: { maxHeight: 600, backgroundColor: "background.paper" },
          }}
          muiTableBodyCellProps={{
            sx: { ...mrtBodyCellSx, borderRight: 1, fontSize: "0.85rem" },
          }}
          muiTableHeadCellProps={{
            sx: {
              ...mrtHeadCellSx,
              fontWeight: "bold",
              fontSize: "0.7rem",
              borderRight: 1,
            },
          }}
          initialState={{ pagination: { pageSize: 50, pageIndex: 0 } }}
          state={{
            columnOrder: allColumnsByTab[currentTab].map(
              (col) => col.accessorKey || col.id,
            ),
          }}
        />
      )}
    </Box>
  );
}
